import React, { memo, useMemo, useEffect } from 'react';
import { Text, Icon, useTheme, Popover } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';

const StatusItem = ({
    index,
    isSelected,
    label,
    nextStatusType,
    prevStatusType,
    scrollHere,
    statusType,
    tooltipDescription,
}) => {
    const theme = useTheme();

    const statusTypeLower = statusType?.toLowerCase() || '';
    const nextStatusTypeLower = nextStatusType?.toLowerCase() || '';
    const prevStatusTypeLower = prevStatusType?.toLowerCase() || '';
    const isError = statusTypeLower === 'error';
    const nextIsError = nextStatusTypeLower === 'error';
    const prevIsActive =
        prevStatusTypeLower &&
        (!prevStatusTypeLower.includes('inactive') ||
            prevStatusTypeLower === 'error' ||
            prevStatusTypeLower === 'unprocessed');
    const isActive =
        statusTypeLower === 'winactive' ||
        (statusTypeLower !== 'wininactive' && !statusTypeLower.includes('inactive')) ||
        statusTypeLower === 'error' ||
        statusTypeLower === 'unprocessed';

    const getStatusTypeBase = useMemo(() => {
        if (statusTypeLower.includes('clock')) return 'clock';
        if (statusTypeLower.includes('check')) return 'check';
        if (statusTypeLower === 'winactive' || statusTypeLower === 'wininactive') return 'win';
        if (statusTypeLower === 'lostactive') return 'lost';
        if (statusTypeLower === 'winlostinactive') return 'win-lost';
        if (statusTypeLower === 'error') return 'error';
        if (statusTypeLower === 'unprocessed') return 'unprocessed';
        return 'check';
    }, [statusTypeLower]);

    const rootClassName = classnames('sage-status-pipeline-widget__status-item', {
        [`sage-status-pipeline-widget__status-item--${getStatusTypeBase}`]: getStatusTypeBase,
        'sage-status-pipeline-widget__status-item--active': isActive,
        'sage-status-pipeline-widget__status-item--inactive': !isActive,
    });

    useEffect(() => {
        if (!isSelected) return;
        scrollHere(index);
    }, [index, isSelected, scrollHere]);

    const iconName = useMemo(() => {
        switch (statusTypeLower) {
            case 'clockactive':
            case 'clockinactive':
                return 'clock';
            case 'checkactive':
            case 'checkinactive':
                return 'check';
            case 'winactive':
            case 'wininactive':
                return 'thumbUp';
            case 'lostactive':
                return 'thumbDown';
            case 'winlostinactive':
                return 'thumbs';
            case 'error':
                return 'close';
            case 'unprocessed':
                return 'hrule';
            default:
                return 'check';
        }
    }, [statusTypeLower]);

    const ItemBubble = (
        <span className="sage-status-pipeline-widget__bubble-icon">
            <Icon name={iconName} size="large" />
        </span>
    );

    const ItemContent = (
        <div className="sage-status-pipeline-widget__status-item-content">
            <div className="sage-status-pipeline-widget__bubble">
                <span
                    className={classnames('sage-status-pipeline-widget__line', {
                        'sage-status-pipeline-widget__line--error-dashed': isError && prevIsActive,
                    })}
                ></span>
                {ItemBubble}
                <span
                    className={classnames('sage-status-pipeline-widget__line', {
                        'sage-status-pipeline-widget__line--error-dashed': nextIsError && isActive,
                        'sage-status-pipeline-widget__line--error-after': isError,
                    })}
                ></span>
            </div>
            <Text
                className="sage-status-pipeline-widget__bubble-title"
                color={
                    isActive ? theme.colors.utility.textPrimary : theme.colors.utility.textDisabled
                }
                type="caption"
            >
                {label}
            </Text>
        </div>
    );

    return (
        <div className={rootClassName}>
            {tooltipDescription ? (
                <Popover content={tooltipDescription} trigger={['hover']}>
                    {ItemContent}
                </Popover>
            ) : (
                ItemContent
            )}
        </div>
    );
};

export default memo(StatusItem);
